'use client';

import { useToast } from '@common/hooks';
import { CustomError, isEnumValue, type ServerResponse } from '@common/utils';
import { useQuery } from '@tanstack/react-query';
import { pick } from 'es-toolkit';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { AuthOpenApi, MEMBER_JOIN_STATUS } from '@/api';
import { AuthClient, AuthQueryOptions, UnauthQueryOptions } from '@/api/v2';
import { ROUTES, STORAGE_KEY } from '@/constants';
import { useAccessToken } from '@/hooks/useAccessToken';
import { useLogout } from '@/hooks/useLogout';
import type { MyInfo } from '@/stores/AuthStore';
import { useAuthStore } from '@/stores/AuthStore';
import { useGlobalStore } from '@/stores/GlobalStore';

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [setIsLogin, setMyInfo, isLogin] = useAuthStore(
    useShallow((state) => [state.setIsLogin, state.setMyInfo, state.isLogin]),
  );

  const setIsGlobalLoading = useGlobalStore(
    (state) => state.setIsGlobalLoading,
  );

  const { accessToken, refreshToken, setAccessToken, setRefreshToken } =
    useAccessToken();

  const { logout } = useLogout();
  const pathname = usePathname();
  const { toastError, toast } = useToast();

  AuthClient.setConfig({
    auth: accessToken ? `Bearer ${accessToken}` : undefined,
  });

  AuthOpenApi.TOKEN = accessToken || undefined;

  const refreshTokenQuery = useQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/member-oauth/sns/refresh_token'
    ]({
      query: { refresh_token: refreshToken ?? '' },
    }),
    enabled:
      !!refreshToken &&
      !accessToken &&
      !!pathname &&
      ![
        ROUTES.CROSS_DOMAIN_AUTH.pathname,
        ROUTES.CROSS_DOMAIN_CALLBACK.pathname,
      ].includes(pathname),
    retry: 3,
    retryDelay: 1_000,
    select: ({ context }) => {
      return {
        accessToken: context?.access_token,
        refreshToken: context?.refresh_token,
      };
    },
  });

  useEffect(() => {
    if (refreshTokenQuery.data?.accessToken) {
      setAccessToken(refreshTokenQuery.data.accessToken);
    }

    if (refreshTokenQuery.data?.refreshToken) {
      setRefreshToken(refreshTokenQuery.data.refreshToken);
    }
  }, [
    refreshTokenQuery.data,
    refreshTokenQuery.isSuccess,
    setAccessToken,
    setRefreshToken,
  ]);

  useEffect(() => {
    if (
      refreshTokenQuery.data?.accessToken === null ||
      refreshTokenQuery.data?.refreshToken === null
    ) {
      console.info('logout by refreshTokenQuery');
      logout({ forInit: true });
    }
  }, [refreshTokenQuery.data, logout]);

  //* 로그인 상태가 없고 리프레시 토큰도 없는 경우 초기화 로그아웃
  useEffect(() => {
    if (
      !accessToken &&
      !refreshToken &&
      !refreshTokenQuery.isLoading &&
      !refreshTokenQuery.isFetching &&
      pathname &&
      ![
        ROUTES.CROSS_DOMAIN_AUTH.pathname,
        ROUTES.CROSS_DOMAIN_CALLBACK.pathname,
      ].includes(pathname)
    ) {
      console.info('init by refreshToken not exist');
      logout({ forInit: true });
    }
  }, [
    accessToken,
    refreshToken,
    refreshTokenQuery.isLoading,
    refreshTokenQuery.isFetching,
    pathname,
    logout,
  ]);

  const myInfoQuery = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/member/profile'](),
    enabled: !!accessToken && !refreshTokenQuery.isFetching,
    select: (data) => {
      const { context } = data;
      const { member_status_cd, save_status_cd } = context ?? {};

      let memberJoinStatus =
        save_status_cd === 'WELLO_MEMBER_FILTER_SAVE_STATUS_WEAKNESS'
          ? MEMBER_JOIN_STATUS.COMPLETE
          : save_status_cd;

      if (
        memberJoinStatus === MEMBER_JOIN_STATUS.COMPLETE &&
        member_status_cd === MEMBER_JOIN_STATUS.NO_PHONE
      ) {
        memberJoinStatus = MEMBER_JOIN_STATUS.NO_PHONE;
      }

      if (!isEnumValue(MEMBER_JOIN_STATUS, memberJoinStatus))
        throw new Error('save_status_cd가 유효하지 않은 값입니다.');

      if (!context) {
        throw new CustomError({ return_message: 'context가 필요합니다.' });
      }

      return {
        ...pick(context, [
          'email',
          'age',
          'gender',
          'region',
          'code_gender',
          'marketing_message_app_push_update_at',
          'marketing_message_email_update_at',
          'marketing_message_mobile_update_at',
        ]),
        nickName: context?.nick_name,
        id: context?.member_oauth_id,
        isVerification: context?.verification_completed_yn,
        profileImage: context?.profile_image,
        birthDate: context?.birth_date,
        memberCode: context?.member_code,
        subRegion: context?.sub_region,
        codeRegion: context?.code_region,
        codeSubRegion: context?.code_sub_region,
        cellPhone: context?.cell_phone,
        memberJoinStatus,
        memberOauth: context?.member_oauth_id,
        marketingMessageReceiverUpdateAt:
          context?.marketing_message_receiver_update_at,
        sensitive_info_yn: context?.sensitive_info_yn,
        notificationOptions: pick(context, [
          'best_contents_receive_yn',
          'hometown_news_receive_yn',
          'community_yn',
          'like_comment_yn',
          'policy_apply_message_receive_yn',
          'marketing_message_receive_yn',
          'popular_policy_receive_yn',
          'reply_comment_yn',
          'report_comment_yn',
          'suggest_policy_receive_yn',
          'marketing_message_mobile_receive_yn',
          'marketing_message_app_push_receive_yn',
          'marketing_message_email_receive_yn',
        ]),
      } satisfies MyInfo;
    },
  });

  useEffect(() => {
    const logoutToast = sessionStorage.getItem(STORAGE_KEY.LOGOUT_TOAST);

    if (!logoutToast) return;

    const toastOptions = JSON.parse(logoutToast);

    toast(toastOptions);

    sessionStorage.removeItem(STORAGE_KEY.LOGOUT_TOAST);
  }, [toast]);

  useEffect(() => {
    if (myInfoQuery.isError) {
      logout({
        redirect: ROUTES.HOME.pathname,
        toastOptions: {
          type: 'fail',
          message: (myInfoQuery.error as ServerResponse).return_message ?? '',
        },
      });

      return;
    }

    if (!myInfoQuery.isFetched || myInfoQuery.isLoading) return;

    if (!myInfoQuery.data) {
      logout({
        toastOptions: {
          type: 'fail',
          message: '내 정보를 불러오는데 실패했습니다.',
        },
      });

      return;
    }

    setMyInfo(myInfoQuery.data);
    setIsLogin(true);
  }, [
    logout,
    myInfoQuery.data,
    myInfoQuery.error,
    myInfoQuery.isError,
    myInfoQuery.isFetched,
    myInfoQuery.isLoading,
    setIsLogin,
    setMyInfo,
    toastError,
  ]);

  useEffect(() => {
    setIsGlobalLoading(isLogin === undefined);
  }, [isLogin, setIsGlobalLoading]);

  const accessTokenStatusQuery = useQuery({
    ...UnauthQueryOptions[
      'post:/wello/v2/allows/member-oauth/token-status-check'
    ]({
      body: {
        access_token: accessToken!,
      },
    }),
    enabled: !!accessToken,
    refetchInterval: 30_000,
    retryDelay: 1_000,
    select: ({ context }) => context?.usable_access_token ?? false,
  });

  useEffect(() => {
    if (accessTokenStatusQuery.isFetched && !accessTokenStatusQuery.data) {
      setAccessToken(null);
    }
  }, [
    accessTokenStatusQuery.data,
    accessTokenStatusQuery.isFetched,
    setAccessToken,
  ]);

  useEffect(() => {
    if (accessTokenStatusQuery.isError) {
      console.info('logout by accessTokenStatusQuery error');

      logout();
    }
  }, [accessTokenStatusQuery.isError, logout]);

  return <>{children}</>;
};
